import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { EMPTY_VALUE, formatCurrency, formatDate, formatNumber } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import type { CreditPack, Plan, PlanRequest, SuccessFee } from "@/services/moderators/generated/model";
import {
  getModeratorsBillingCreditPacksListQueryKey,
  getModeratorsBillingPlansListQueryKey,
  useModeratorsBillingCreditPacksCreate,
  useModeratorsBillingCreditPacksList,
  useModeratorsBillingCreditPacksPartialUpdate,
  useModeratorsBillingPlansCreate,
  useModeratorsBillingPlansList,
  useModeratorsBillingPlansPartialUpdate,
  useModeratorsBillingSuccessFeesList,
} from "@/services/moderators/generated/moderators-billing/moderators-billing";
import "./ModeratorPage.css";
import "./LeadSources.css";

const LIST_PARAMS = { page_size: 100 };

const ModeratorBillingPage = () => (
  <ModeratorLayout>{(moderator) => <BillingContent canManage={moderator.role === "SUPERMODERATOR"} />}</ModeratorLayout>
);

/**
 * WP2.4 billing administration: plans and credit packs (prices, limits and
 * pack sizes are data, set here by super moderators), and the success-fee
 * records on won, attributed deals (recorded only — nothing is charged).
 */
const BillingContent = ({ canManage }: { canManage: boolean }) => {
  const { t } = useTranslation(["moderator", "common"]);
  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("billing.title")}</h1>
          <p className="mod-page__subtitle">{t("billing.subtitle")}</p>
        </div>
      </header>
      {canManage ? null : <p className="mod-page__card-meta">{t("billing.superOnly")}</p>}
      <PlansSection canManage={canManage} />
      <PacksSection canManage={canManage} />
      <SuccessFeesSection />
    </div>
  );
};

/** "" → null (unlimited); otherwise a non-negative integer. */
const parseLimit = (value: string): number | null => (value.trim() === "" ? null : Number(value));
const limitText = (value: number | null | undefined): string => (value == null ? "" : String(value));

/**
 * Per-currency Stripe price ids are stored as a `{code: price_id}` map on
 * the backend; super moderators edit them as one line per currency in the
 * form `CAD=price_...` / `USD=price_...` / `AED=price_...`. Blank values
 * drop their currency entry.
 */
const serializePriceIds = (ids: Record<string, string> | undefined | null): string =>
  Object.entries(ids ?? {})
    .filter(([code]) => code.trim() !== "")
    .map(([code, id]) => `${code.toUpperCase()}=${id}`)
    .join("\n");

const parsePriceIds = (text: string): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const eq = line.indexOf("=");
    if (eq <= 0) continue;
    const code = line.slice(0, eq).trim().toUpperCase();
    const id = line.slice(eq + 1).trim();
    if (code && id) out[code] = id;
  }
  return out;
};

/** Human summary of per-currency price ids for the moderator table: e.g.
 * "CAD, USD" (ignoring blank slots). "—" when none. */
const priceIdsSummary = (ids: Record<string, string> | undefined | null): string => {
  const codes = Object.entries(ids ?? {})
    .filter(([, id]) => id && id.trim() !== "")
    .map(([code]) => code.toUpperCase())
    .sort();
  return codes.length === 0 ? EMPTY_VALUE : codes.join(", ");
};

interface PlanForm {
  code: string;
  name: string;
  usesCredits: boolean;
  monthlyRequestLimit: string;
  monthlyMatchLimit: string;
  activeDealLimit: string;
  successFeePercent: string;
  stripePriceId: string;
  stripePriceIds: string;
}

const EMPTY_PLAN: PlanForm = {
  code: "",
  name: "",
  usesCredits: false,
  monthlyRequestLimit: "",
  monthlyMatchLimit: "",
  activeDealLimit: "",
  successFeePercent: "",
  stripePriceId: "",
  stripePriceIds: "",
};

const PlansSection = ({ canManage }: { canManage: boolean }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useModeratorsBillingPlansList(LIST_PARAMS);
  const plans = unwrapPaginated<Plan>(data);
  const [form, setForm] = useState<PlanForm>(EMPTY_PLAN);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setForm(EMPTY_PLAN);
    setEditingId(null);
    setFormError(null);
  };
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: getModeratorsBillingPlansListQueryKey(LIST_PARAMS) });
    reset();
  };
  const onError = (err: unknown) => setFormError(getApiErrorMessage(err, t("billing.saveError")));
  const create = useModeratorsBillingPlansCreate({ mutation: { onSuccess, onError } });
  const update = useModeratorsBillingPlansPartialUpdate({ mutation: { onSuccess, onError } });

  const set = <K extends keyof PlanForm>(key: K, value: PlanForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      setFormError(t("billing.codeNameRequired"));
      return;
    }
    const body: PlanRequest = {
      code: form.code.trim(),
      name: form.name.trim(),
      usesCredits: form.usesCredits,
      monthlyRequestLimit: parseLimit(form.monthlyRequestLimit),
      monthlyMatchLimit: parseLimit(form.monthlyMatchLimit),
      activeDealLimit: parseLimit(form.activeDealLimit),
      successFeePercent: form.successFeePercent.trim() === "" ? null : form.successFeePercent.trim(),
      stripePriceId: form.stripePriceId.trim(),
      stripePriceIds: parsePriceIds(form.stripePriceIds),
    };
    if (editingId !== null) update.mutate({ id: editingId, data: body });
    else create.mutate({ data: body });
  };

  const edit = (plan: Plan) => {
    setEditingId(plan.id);
    setFormError(null);
    setForm({
      code: plan.code,
      name: plan.name,
      usesCredits: plan.usesCredits ?? false,
      monthlyRequestLimit: limitText(plan.monthlyRequestLimit),
      monthlyMatchLimit: limitText(plan.monthlyMatchLimit),
      activeDealLimit: limitText(plan.activeDealLimit),
      successFeePercent: plan.successFeePercent ?? "",
      stripePriceId: plan.stripePriceId ?? "",
      stripePriceIds: serializePriceIds(plan.stripePriceIds as Record<string, string> | null | undefined),
    });
  };

  const limitCell = (value: number | null | undefined) =>
    value == null ? t("billing.unlimited") : formatNumber(value);
  const saving = create.isPending || update.isPending;

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("billing.plans.title")}</h2>
      <p className="mod-page__card-meta">{t("billing.plans.hint")}</p>
      {canManage ? (
        <form className="mod-sources__form" onSubmit={submit}>
          <input
            className="mod-page__input"
            placeholder={t("billing.fields.code")}
            aria-label={t("billing.fields.code")}
            value={form.code}
            maxLength={64}
            onChange={(e) => set("code", e.target.value)}
          />
          <input
            className="mod-page__input"
            placeholder={t("billing.fields.name")}
            aria-label={t("billing.fields.name")}
            value={form.name}
            maxLength={255}
            onChange={(e) => set("name", e.target.value)}
          />
          <input
            className="mod-page__input"
            type="number"
            min={0}
            placeholder={t("billing.fields.requestLimit")}
            aria-label={t("billing.fields.requestLimit")}
            value={form.monthlyRequestLimit}
            onChange={(e) => set("monthlyRequestLimit", e.target.value)}
          />
          <input
            className="mod-page__input"
            type="number"
            min={0}
            placeholder={t("billing.fields.matchLimit")}
            aria-label={t("billing.fields.matchLimit")}
            value={form.monthlyMatchLimit}
            onChange={(e) => set("monthlyMatchLimit", e.target.value)}
          />
          <input
            className="mod-page__input"
            type="number"
            min={0}
            placeholder={t("billing.fields.dealLimit")}
            aria-label={t("billing.fields.dealLimit")}
            value={form.activeDealLimit}
            onChange={(e) => set("activeDealLimit", e.target.value)}
          />
          <input
            className="mod-page__input"
            type="number"
            min={0}
            max={100}
            step="0.01"
            placeholder={t("billing.fields.feePercent")}
            aria-label={t("billing.fields.feePercent")}
            value={form.successFeePercent}
            onChange={(e) => set("successFeePercent", e.target.value)}
          />
          <input
            className="mod-page__input"
            placeholder={t("billing.fields.stripePrice")}
            aria-label={t("billing.fields.stripePrice")}
            value={form.stripePriceId}
            onChange={(e) => set("stripePriceId", e.target.value)}
          />
          <textarea
            className="mod-page__input"
            rows={3}
            placeholder={t("billing.fields.stripePriceIdsPlaceholder")}
            aria-label={t("billing.fields.stripePriceIds")}
            value={form.stripePriceIds}
            onChange={(e) => set("stripePriceIds", e.target.value)}
          />
          <label className="mod-page__card-meta">
            <input type="checkbox" checked={form.usesCredits} onChange={(e) => set("usesCredits", e.target.checked)} />{" "}
            {t("billing.fields.usesCredits")}
          </label>
          <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={saving}>
            {saving
              ? t("common:actions.saving")
              : editingId !== null
                ? t("common:actions.save")
                : t("billing.plans.add")}
          </button>
          {editingId !== null ? (
            <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={reset}>
              {t("common:actions.cancel")}
            </button>
          ) : null}
        </form>
      ) : null}
      {formError ? (
        <p className="mod-page__error" role="alert">
          {formError}
        </p>
      ) : null}
      {isLoading ? <div className="mod-page__loading">{t("billing.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("billing.loadError")}</div> : null}
      {!isLoading && !error && plans.length === 0 ? (
        <div className="mod-page__empty">{t("billing.plans.empty")}</div>
      ) : null}
      {plans.length > 0 ? (
        <div className="mod-sources__table-wrap">
          <table className="mod-sources__table">
            <thead>
              <tr>
                <th>{t("billing.fields.code")}</th>
                <th>{t("billing.fields.name")}</th>
                <th>{t("billing.fields.requestLimit")}</th>
                <th>{t("billing.fields.matchLimit")}</th>
                <th>{t("billing.fields.dealLimit")}</th>
                <th>{t("billing.fields.usesCredits")}</th>
                <th>{t("billing.fields.feePercent")}</th>
                <th>{t("billing.fields.currencies")}</th>
                <th>{t("common:labels.status")}</th>
                {canManage ? <th>{t("common:labels.actions")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>{plan.code}</td>
                  <td>{plan.name}</td>
                  <td>{limitCell(plan.monthlyRequestLimit)}</td>
                  <td>{limitCell(plan.monthlyMatchLimit)}</td>
                  <td>{limitCell(plan.activeDealLimit)}</td>
                  <td>{plan.usesCredits ? t("billing.yes") : t("billing.no")}</td>
                  <td>{plan.successFeePercent ?? EMPTY_VALUE}</td>
                  <td>{priceIdsSummary(plan.stripePriceIds as Record<string, string> | null | undefined)}</td>
                  <td>
                    <span
                      className={`mod-page__chip ${plan.active === false ? "mod-page__chip--warning" : "mod-page__chip--success"}`}
                    >
                      {plan.active === false ? t("shared.inactive") : t("shared.active")}
                    </span>
                  </td>
                  {canManage ? (
                    <td>
                      <div className="mod-sources__actions">
                        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => edit(plan)}>
                          {t("common:actions.edit")}
                        </button>
                        <button
                          type="button"
                          className="mod-page__btn mod-page__btn--ghost"
                          disabled={update.isPending}
                          onClick={() => update.mutate({ id: plan.id, data: { active: plan.active === false } })}
                        >
                          {plan.active === false ? t("shared.activate") : t("shared.deactivate")}
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
};

const PacksSection = ({ canManage }: { canManage: boolean }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useModeratorsBillingCreditPacksList(LIST_PARAMS);
  const packs = unwrapPaginated<CreditPack>(data);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [credits, setCredits] = useState("");
  const [priceId, setPriceId] = useState("");
  const [priceIds, setPriceIds] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setCode("");
    setName("");
    setCredits("");
    setPriceId("");
    setPriceIds("");
    setEditingId(null);
    setFormError(null);
  };
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: getModeratorsBillingCreditPacksListQueryKey(LIST_PARAMS) });
    reset();
  };
  const onError = (err: unknown) => setFormError(getApiErrorMessage(err, t("billing.saveError")));
  const create = useModeratorsBillingCreditPacksCreate({ mutation: { onSuccess, onError } });
  const update = useModeratorsBillingCreditPacksPartialUpdate({ mutation: { onSuccess, onError } });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) {
      setFormError(t("billing.codeNameRequired"));
      return;
    }
    const n = Number(credits);
    if (!Number.isInteger(n) || n < 1) {
      setFormError(t("billing.packs.creditsInvalid"));
      return;
    }
    const body = {
      code: code.trim(),
      name: name.trim(),
      credits: n,
      stripePriceId: priceId.trim(),
      stripePriceIds: parsePriceIds(priceIds),
    };
    if (editingId !== null) update.mutate({ id: editingId, data: body });
    else create.mutate({ data: body });
  };

  const edit = (pack: CreditPack) => {
    setEditingId(pack.id);
    setCode(pack.code);
    setName(pack.name);
    setCredits(String(pack.credits));
    setPriceId(pack.stripePriceId ?? "");
    setPriceIds(serializePriceIds(pack.stripePriceIds as Record<string, string> | null | undefined));
    setFormError(null);
  };
  const saving = create.isPending || update.isPending;

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("billing.packs.title")}</h2>
      <p className="mod-page__card-meta">{t("billing.packs.hint")}</p>
      {canManage ? (
        <form className="mod-sources__form" onSubmit={submit}>
          <input
            className="mod-page__input"
            placeholder={t("billing.fields.code")}
            aria-label={t("billing.fields.code")}
            value={code}
            maxLength={64}
            onChange={(e) => setCode(e.target.value)}
          />
          <input
            className="mod-page__input"
            placeholder={t("billing.fields.name")}
            aria-label={t("billing.fields.name")}
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="mod-page__input"
            type="number"
            min={1}
            placeholder={t("billing.fields.credits")}
            aria-label={t("billing.fields.credits")}
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
          />
          <input
            className="mod-page__input"
            placeholder={t("billing.fields.stripePrice")}
            aria-label={t("billing.fields.stripePrice")}
            value={priceId}
            onChange={(e) => setPriceId(e.target.value)}
          />
          <textarea
            className="mod-page__input"
            rows={3}
            placeholder={t("billing.fields.stripePriceIdsPlaceholder")}
            aria-label={t("billing.fields.stripePriceIds")}
            value={priceIds}
            onChange={(e) => setPriceIds(e.target.value)}
          />
          <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={saving}>
            {saving
              ? t("common:actions.saving")
              : editingId !== null
                ? t("common:actions.save")
                : t("billing.packs.add")}
          </button>
          {editingId !== null ? (
            <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={reset}>
              {t("common:actions.cancel")}
            </button>
          ) : null}
        </form>
      ) : null}
      {formError ? (
        <p className="mod-page__error" role="alert">
          {formError}
        </p>
      ) : null}
      {isLoading ? <div className="mod-page__loading">{t("billing.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("billing.loadError")}</div> : null}
      {!isLoading && !error && packs.length === 0 ? (
        <div className="mod-page__empty">{t("billing.packs.empty")}</div>
      ) : null}
      {packs.length > 0 ? (
        <div className="mod-sources__table-wrap">
          <table className="mod-sources__table">
            <thead>
              <tr>
                <th>{t("billing.fields.code")}</th>
                <th>{t("billing.fields.name")}</th>
                <th className="mod-sources__count">{t("billing.fields.credits")}</th>
                <th>{t("billing.fields.stripePrice")}</th>
                <th>{t("billing.fields.currencies")}</th>
                <th>{t("common:labels.status")}</th>
                {canManage ? <th>{t("common:labels.actions")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {packs.map((pack) => (
                <tr key={pack.id}>
                  <td>{pack.code}</td>
                  <td>{pack.name}</td>
                  <td className="mod-sources__count">{formatNumber(pack.credits)}</td>
                  <td>{pack.stripePriceId || EMPTY_VALUE}</td>
                  <td>{priceIdsSummary(pack.stripePriceIds as Record<string, string> | null | undefined)}</td>
                  <td>
                    <span
                      className={`mod-page__chip ${pack.active === false ? "mod-page__chip--warning" : "mod-page__chip--success"}`}
                    >
                      {pack.active === false ? t("shared.inactive") : t("shared.active")}
                    </span>
                  </td>
                  {canManage ? (
                    <td>
                      <div className="mod-sources__actions">
                        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => edit(pack)}>
                          {t("common:actions.edit")}
                        </button>
                        <button
                          type="button"
                          className="mod-page__btn mod-page__btn--ghost"
                          disabled={update.isPending}
                          onClick={() => update.mutate({ id: pack.id, data: { active: pack.active === false } })}
                        >
                          {pack.active === false ? t("shared.activate") : t("shared.deactivate")}
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
};

const SuccessFeesSection = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const { data, isLoading, error } = useModeratorsBillingSuccessFeesList(LIST_PARAMS);
  const fees = unwrapPaginated<SuccessFee>(data);

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("billing.fees.title")}</h2>
      <p className="mod-page__card-meta">{t("billing.fees.hint")}</p>
      {isLoading ? <div className="mod-page__loading">{t("billing.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("billing.loadError")}</div> : null}
      {!isLoading && !error && fees.length === 0 ? (
        <div className="mod-page__empty">{t("billing.fees.empty")}</div>
      ) : null}
      {fees.length > 0 ? (
        <div className="mod-sources__table-wrap">
          <table className="mod-sources__table">
            <thead>
              <tr>
                <th>{t("billing.fees.deal")}</th>
                <th>{t("billing.fees.buyer")}</th>
                <th>{t("billing.fees.supplier")}</th>
                <th>{t("billing.fees.wonAt")}</th>
                <th className="mod-sources__count">{t("billing.fees.dealValue")}</th>
                <th className="mod-sources__count">{t("billing.fields.feePercent")}</th>
                <th className="mod-sources__count">{t("billing.fees.amount")}</th>
                <th>{t("common:labels.status")}</th>
              </tr>
            </thead>
            <tbody>
              {fees.map((fee) => (
                <tr key={fee.id}>
                  <td>
                    <Link to={`/moderator/deals/${fee.dealId}`}>{t("billing.fees.dealLink", { id: fee.dealId })}</Link>
                  </td>
                  <td>{fee.companyName}</td>
                  <td>{fee.supplierName}</td>
                  <td>{fee.wonAt ? formatDate(fee.wonAt) : EMPTY_VALUE}</td>
                  <td className="mod-sources__count">{formatCurrency(Number(fee.dealValue), fee.currency)}</td>
                  <td className="mod-sources__count">{fee.ratePercent ?? t("billing.fees.noRate")}</td>
                  <td className="mod-sources__count">
                    {fee.amount != null ? formatCurrency(Number(fee.amount), fee.currency) : EMPTY_VALUE}
                  </td>
                  <td>
                    <span
                      className={`mod-page__chip ${fee.status === "void" ? "mod-page__chip--warning" : "mod-page__chip--success"}`}
                    >
                      {t(`billing.fees.status.${fee.status ?? "pending"}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
};

export default ModeratorBillingPage;
